import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, Heart, Mail, UserCircle2, Users } from "lucide-react";

import { api, photoUri } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loading } from "@/components/ui";
import { Logo } from "@/components/logo";

export function useBadges(enabled) {
  return useQuery({
    queryKey: ["unread"],
    queryFn: () => api("/notifications/unread-count"),
    refetchInterval: 15000,
    enabled,
  });
}

const TABS = [
  { to: "/discover", key: "discover", title: "Discover", Icon: Heart },
  { to: "/requests", key: "requests", title: "Requests", Icon: Mail },
  { to: "/connections", key: "connections", title: "Connections", Icon: Users },
  { to: "/notifications", key: "notifications", title: "Alerts", Icon: Bell },
  { to: "/profile", key: "profile", title: "Profile", Icon: UserCircle2 },
];

function Badge({ n, testId }) {
  if (!n) return null;
  return <span data-testid={testId} className="absolute -right-2 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#D48D93] px-1 text-[10px] font-bold text-[#2A2727]">{n > 99 ? "99+" : n}</span>;
}

/** Member area chrome: top bar on tablet/desktop, bottom tab bar on phones (mirrors the 5 mobile tabs). */
export function MemberLayout() {
  const { status, user } = useAuth();
  const { pathname } = useLocation();
  const { data } = useBadges(status === "authed");
  if (status === "loading") return <Loading />;
  if (status === "guest") return <Navigate to="/landing" replace />;
  if (user && !user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  const badge = (k) => (k === "requests" ? data?.pending_received : k === "notifications" ? data?.unread_count : 0);

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-border bg-[#FDFBF7]/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 md:px-8">
          <Link to="/discover" data-testid="nav-logo" className="rounded-full"><Logo /></Link>
          <nav className="ml-auto hidden items-center gap-1 md:flex" aria-label="Main">
            {TABS.map((t) => (
              <NavLink key={t.key} to={t.to} data-testid={`tab-${t.key}`} aria-label={t.title} title={t.title}
                className={({ isActive }) => `relative flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-foreground hover:bg-[#F6E3E4]"}`}>
                <span className="relative"><t.Icon className="h-[18px] w-[18px]" /><Badge n={badge(t.key)} testId={`tab-badge-${t.key}`} /></span>
                <span className="hidden lg:inline">{t.title}</span>
              </NavLink>
            ))}
          </nav>
          {user?.main_photo_url ? (
            <Link to="/profile" data-testid="nav-avatar" className="ml-auto hidden h-10 w-10 overflow-hidden rounded-full border-2 border-[#D48D93] md:ml-0 md:block" aria-label="Your profile">
              <img src={photoUri(user.main_photo_url)} alt="" className="h-full w-full object-cover" />
            </Link>
          ) : null}
        </div>
      </header>
      <main key={pathname} className="fade-up mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-10">
        <Outlet />
      </main>
      <nav aria-label="Tabs" className="fixed inset-x-0 bottom-0 z-40 grid h-16 grid-cols-5 border-t border-border bg-white md:hidden">
        {TABS.map((t) => (
          <NavLink key={t.key} to={t.to} data-testid={`tab-mobile-${t.key}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${isActive ? "text-primary" : "text-[#756E69]"}`}>
            <span className="relative"><t.Icon className="h-6 w-6" /><Badge n={badge(t.key)} testId={`tab-mobile-badge-${t.key}`} /></span>
            {t.title}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
